import type { AiFilterPreviewType } from "../components/AiFilters/types";
import { objectToQueryParams } from "./ApiHelpers";
import { axiosEjFetch as axiosFetch } from "./axiosEjHelpers";

export async function getAiFilterList() {
  return axiosFetch.get("/api/aiFilter");
}

export enum PreviewGenerationStatusEnum {
  NotGenerated = 0,
  PreviewGenerating = 1,
  PreviewGenerated = 2,
  Error = 3,
}

type PreviewGenerationErrorType = {
  message: string;
  time: string;
};

export type AiFilterStatusType = {
  previewGenerationStatus: PreviewGenerationStatusEnum;
  errors: PreviewGenerationErrorType[];
};

export type ProviderAssignmentType = {
  providerId: string;
  isGlobal: boolean;
};

export type AiFilterReferenceImageType = {
  id: string;
  url: string;
};

export type AiFilterResponseType = {
  id: string;
  name: string;
  prompt: string;
  supplier: string;
  type: number;
  eventType?: number | null;
  /** True if the filter also generates a video (its prompt has a #VEO# section). */
  hasVideo: boolean;
  filterOrientation: number;
  isDeleted: boolean;
  status: AiFilterStatusType;
  groupFilterCount: number;
  providerAssignments: ProviderAssignmentType[];
  referenceImages: AiFilterReferenceImageType[];
  previews: AiFilterPreviewType[];
  reservationId: string | null;
  inputImageId?: string;
  inputImageUrl?: string;
  preview16x9?: string;
  preview16x9Url?: string;
  preview16x9ThumbnailUrl?: string;
  preview9x16?: string;
  preview9x16Url?: string;
  preview9x16ThumbnailUrl?: string;
  preview1x1?: string;
  preview1x1Url?: string;
  preview1x1ThumbnailUrl?: string;
  preview4x3?: string;
  preview4x3Url?: string;
  preview4x3ThumbnailUrl?: string;
  preview3x4?: string;
  preview3x4Url?: string;
  preview3x4ThumbnailUrl?: string;
};

export async function getAiFilterById(
  id: string
): Promise<AiFilterResponseType> {
  return axiosFetch.get(`/api/aiFilter/${id}`);
}

export type CreateOrUpdateAiFilterType = {
  id?: string;
  name: string;
  prompt: string;
  imageFiles?: FileList;
  type: number;
  eventType?: number | null;
  groupFilterCount: number;
  filterOrientation: number;
  providerAssignments?: ProviderAssignmentType[];
  imageIds: string[] | [];
  referenceUrls: string[] | [];
  reservationId?: string | null;
  inputImageId?: string;
  inputImageFile?: File;
};

export async function postAiFilter(body: CreateOrUpdateAiFilterType) {
  const headers = {
    "Content-Type": "multipart/form-data",
  };

  const formData = new FormData();

  formData.append("id", body.id || "");
  formData.append("name", body.name);
  formData.append("prompt", body.prompt);
  formData.append("supplier", "EventJinny"); // TODO remove after the backend removed it too
  formData.append("type", body.type.toString());
  formData.append("groupFilterCount", body.groupFilterCount.toString());
  formData.append("filterOrientation", body.filterOrientation.toString());
  formData.append("reservationId", body.reservationId || "");
  if (body.eventType !== null && body.eventType !== undefined) {
    formData.append("eventType", body.eventType.toString());
  }
  if (body.imageIds && body.imageIds.length) {
    body.imageIds.forEach((id, index) => {
      formData.append(`ImageIds[${index}]`, id);
    });
  }

  formData.append(
    "providerAssignments",
    JSON.stringify(body.providerAssignments)
  );
  if (body.imageFiles && body.imageFiles?.length) {
    for (const file of body.imageFiles) {
      formData.append("imageFiles", file);
    }
  }

  if (body.inputImageFile) {
    formData.append("inputImageFile", body.inputImageFile);
  }

  if (body.inputImageId) {
    formData.append("inputImageId", body.inputImageId);
  }

  return axiosFetch.post("/api/aiFilter", formData, headers);
}

export async function deleteAiFilter(id: string) {
  return axiosFetch.del(`/api/aiFilter/${id}`);
}

export type AIFilterParamsType = {
  nameFilters?: string[];
  reservationId?: string;
  reservationProviderId?: string;
};

export async function getAiFiltersByName(
  params?: AIFilterParamsType
): Promise<AiFilterResponseType[]> {
  const queryParams = objectToQueryParams(params);
  return axiosFetch.safeGet<any, any, AiFilterResponseType[]>(
    `/api/aiFilter/filtersByName${queryParams}`
  );
}
export type GeneratePreviewRequest = {
  aspectRatios: string[];
  prompt?: string;
  promptSubstitutions?: Record<string, string>;
  saveToFilter?: boolean;
  inputImageBase64?: string;
  reservationId?: string;
};

export type GeneratePreviewResponse = {
  success: boolean;
  generatedPreviews: Record<string, string>;
  isVideoPreviews?: Record<string, boolean>;
  videoDurations?: Record<string, number>;
  validationErrors?: string[];
};

export async function generatePreview(
  filterId: string,
  aspectRatios: string[],
  prompt?: string,
  promptSubstitutions?: Record<string, string>,
  saveToFilter: boolean = true,
  inputImageBase64?: string,
  reservationId?: string
): Promise<GeneratePreviewResponse> {
  const response = await axiosFetch.post<
    GeneratePreviewRequest,
    undefined,
    undefined
  >(`/api/aiFilter/${filterId}/generatePreview`, {
    aspectRatios,
    prompt,
    promptSubstitutions,
    saveToFilter,
    inputImageBase64,
    reservationId,
  });
  return response.data;
}
export async function getAiFiltersByNamePublic(
  params?: AIFilterParamsType
): Promise<AiFilterResponseType[]> {
  const { getAppConfiguration } = await import("appConfig");
  const { default: axios } = await import("axios");
  const appConfiguration = await getAppConfiguration();
  const queryParams = objectToQueryParams(params);
  const response = await axios.get<AiFilterResponseType[]>(
    `${appConfiguration.apiUrl}/api/aiFilter/filtersByName${queryParams}`
  );
  return response.data;
}
